import React, {
  useEffect,
  useState,
  useImperativeHandle,
  useCallback,
  useContext,
} from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Input, Text, Radio, Tag } from "react_reusable_components";

import CustomSelect from "../../components/CustomSelect/CustomSelect";
import Remarks from "../../components/Remarks";
import AudioContainer from "../AudioContainer/AudioContainer";
import MediaContainer from "../MediaContainer";
import { ThreeRow, TwoColumn, TwoRow } from "../../core/layouts";
import AuditService from "../../services/audit-service";
import QuestionStyle from "../AddQuestionnaireQuestionContainer/AddQuestionnaireQuestionContainerStyle.module.scss";
import { getIsAudit, getLocalizedMessage } from "../../UtilityFunctions/utils";
import ThreeColumn from "../../core/layouts/ThreeColumn";

import {
  EVALUATION_STATUS_CONSTANTS,
  EVALUATION_STATUS_OPTIONS,
  questionnaireSelectOptions,
  QUESTION_TYPE_CONSTANTS,
  userRole,
} from "../../constants";
import variables from "../../themes/base/styles/variables";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { RemoteAuditContext } from "../../globalContext/remoteAudit/remoteAuditProvider";
import {
  saveInprogressEvidenceInstance,
  setQuestionPayloadInStore,
} from "../../globalContext/remoteAudit/remoteAuditActions";
import { AuthContext } from "../../globalContext/auth/authProvider";
import useResponsive from "../../core/hooks/useResponsive";
import AddCounterMeasureContainer from "../AddCounterMeasureContainer";

const AuditQuestionnaireContainerStyle = {
  mainTwoColumnStyle: {
    gap: variables.gapSmaller,
    justifyContent: "space-between",
    overflow: "unset",
  },
  questionWrapperStyle: {
    overflow: "unset",
    gap: variables.gapSmaller,
  },
  inputWrapperStyle: {
    display: variables.dFlex,
    width: variables.fullWidth,
  },
  responseOptionsWrapperStyle: {
    flexDirection: "column",
    gap: variables.gapSmaller,
  },
  radioStyle: {
    width: "fit-content",
  },
  radioLabelStyle: {
    textTransform: "capitalize",
    fontSize: variables.fontSizeLarge,
  },
  overflowUnsetStyle: { overflow: "unset" },
  smallerGapStyle: { gap: variables.gapSmaller },
  customSelectLabelStyle: {
    marginTop: variables.marginQuarter,
    height: variables.lineHeightXlarge,
    alignItems: variables.alignCenter,
  },
  indentShiftStyle: {
    marginLeft: variables.marginMedium2,
  },
  tagStyle: {
    borderRadius: variables.radiusXXLarge,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
    fontSize: variables.fontSizeNormal,
    color: variables.textDefault,
    padding: `${variables.marginXSmallest} ${variables.marginMediumSmall}`,
    height: variables.tagHeight,
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    border: variables.tagBorder,
    background: variables.lightCraftingBlue,
    width: "fit-content",
    textTransform: "capitalize",
    margin: 0,
  },
};

const AuditQuestionnaireContainer = ({
  question,
  evidenceInProgress,
  setEvidenceInProgress,
  mode,
  setBorderAlert = () => {}, //NOSONAR
  outerRef,
}) => {
  const intl = useIntl();
  const responsive = useResponsive();
  const { id, auditId, auditMedium } = useParams();
  const auditDispatch = useContext(AuditContext)[1];
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [remoteAuditState, remoteAuditDispatch] =
    useContext(RemoteAuditContext);
  const authState = useContext(AuthContext)[0];
  const [selectedValues, setSelectedValues] = useState([]);
  const [payloadChanged, setPayloadChanged] = useState(false);
  const [questionPayload, setQuestionPayload] = useState({
    auditId: parseInt(id || auditId),
    questionId: question?.id,
    response: [],
  });
  const [snapShotsUploaded, setSnapShotsUploaded] = useState([]);
  const [videosUploaded, setVideosUploaded] = useState([]);
  const [audioUploaded, setAudioUploaded] = useState([]);

  const isDnsd = window.location.href.includes("dnsd/audit/self/");

  const isAudit = getIsAudit();

  const isAuditPreview = window.location.href.includes("audit-preview");
  const isRemote = window.location.href.includes("remote");

  const handleSaveQuestionnaire = useCallback(() => {
    const filteredResponse = questionPayload?.response?.filter(
      (item) => item?.response
    );
    const { response, counterMeasure, evidence, responseId, ...rest } =
      questionPayload;
    let updatedData = { ...rest };
    if (filteredResponse?.length) {
      updatedData = {
        ...rest,
        response: filteredResponse,
      };
    }
    if (
      rest.evaluationStatus === EVALUATION_STATUS_CONSTANTS.IMPROVEMENT ||
      rest.evaluationStatus === EVALUATION_STATUS_CONSTANTS.NG
    ) {
      updatedData = { ...updatedData, counterMeasure };
    }

    if (isDnsd && updatedData.evaluationStatus) {
      delete updatedData.evaluationStatus;
    }

    if (isDnsd && updatedData.counterMeasure) {
      delete updatedData.counterMeasure;
    }

    AuditService.saveQuestionResponse({
      intl,
      payload: updatedData,
    })
      .then(() => {
        if (updatedData.evaluationStatus)
          AuditService.getAuditQuestionnaireById({
            auditDispatch,
            auditId: id || auditId,
            intl,
          });
      })
      .catch(() => {}); //NOSONAR
    setPayloadChanged(false);
  }, [questionPayload]);

  useImperativeHandle(
    outerRef,
    () => {
      return {
        saveQuestion() {
          payloadChanged && handleSaveQuestionnaire();
          payloadChanged &&
            AuditService.getAuditQuestionnaireById({
              auditDispatch,
              loaderDispatch,
              auditId: id || auditId,
              intl,
            });
        },
      };
    },
    [handleSaveQuestionnaire]
  );

  const handleCheckboxChange = (e) => {
    const currentComponentId = question?.questionType?.filter(
      (qItem) => qItem.type !== QUESTION_TYPE_CONSTANTS.TEXTBOX
    )?.[0]?.id;

    let newSelectedValues = [];
    if (selectedValues.includes(e)) {
      setSelectedValues(selectedValues.filter((item) => item !== e));
      newSelectedValues = selectedValues.filter((item) => item !== e);
    } else {
      if (
        question?.questionType?.some(
          (item) => item.type === QUESTION_TYPE_CONSTANTS.SINGLE_SELECT
        )
      ) {
        setSelectedValues([e]);
        newSelectedValues = [e];
      } else {
        setSelectedValues((prev) => [...prev, e]);
        newSelectedValues = [...selectedValues, e];
      }
    }

    if (newSelectedValues?.length) {
      handleSetResponse(
        newSelectedValues?.length > 1
          ? newSelectedValues
          : newSelectedValues[0],
        currentComponentId
      );
    } else {
      handleSetResponse("", currentComponentId);
    }
  };

  const handleTextBoxChange = (e) => {
    const currentComponentId = question?.questionType?.filter(
      (qItem) => qItem.type === QUESTION_TYPE_CONSTANTS.TEXTBOX
    )?.[0]?.id;

    handleSetResponse(e.target.value, currentComponentId);
  };

  useEffect(() => {
    if (!payloadChanged) setPayloadChanged(true);
  }, [
    questionPayload.textResponse,
    questionPayload.evaluationStatus,
    questionPayload.counterMeasure,
    questionPayload.remarks,
    questionPayload.response,
    questionPayload.response?.length,
  ]);
  const handleKeyValuePairs = (key, value) => {
    setQuestionPayload((prev) => ({
      ...prev,
      [key]: value,
    }));
    // if (!payloadChanged) setPayloadChanged(true);
  };

  const handleSetResponse = (res, currentComponentId) => {
    const existingResponseIndex = questionPayload?.response?.findIndex(
      (responseItem) => responseItem.componentId === currentComponentId
    );

    if (existingResponseIndex !== -1) {
      // If response for current component already exists, update it
      setQuestionPayload((prevPayload) => {
        const updatedResponse = [...prevPayload.response];
        updatedResponse[existingResponseIndex].response = res;

        return {
          ...prevPayload,
          response: updatedResponse,
        };
      });
    } else {
      // If response for current component doesn't exist, append a new response object
      const newResponse = {
        componentId: currentComponentId,
        response: res,
      };
      setQuestionPayload((prevPayload) => ({
        ...prevPayload,
        response: [...prevPayload.response, newResponse],
      }));
    }

    // if (!payloadChanged) setPayloadChanged(true);
  };

  useEffect(() => {
    if (remoteAuditState?.isEvidenceCompleted) {
      const newEvidence = remoteAuditState?.evidenceInProgressInstance;
      let updatedNewEvidence = [];
      if (remoteAuditState?.evidenceInProgressInstance?.type === "video") {
        updatedNewEvidence = videosUploaded?.length
          ? [...videosUploaded, newEvidence]
          : [newEvidence];
        setVideosUploaded(updatedNewEvidence);
      } else {
        updatedNewEvidence = audioUploaded?.length
          ? [...audioUploaded, newEvidence?.documentId]
          : [newEvidence?.documentId];
        setAudioUploaded(updatedNewEvidence.filter(Boolean));
      }
      onMediaChanged(
        updatedNewEvidence,
        newEvidence?.type === "video" ? "video" : "audio"
      );
      remoteAuditDispatch(saveInprogressEvidenceInstance({}));
    }
  }, [remoteAuditState?.isEvidenceCompleted]);

  const setAllValuesOnMount = () => {
    setQuestionPayload((prev) => ({
      questionId: question?.id,
      auditId: parseInt(id || auditId),
      response:
        question?.questionType?.map?.((item) => ({
          componentId: item.id,
          response: item?.response,
        })) || [],
      ...question?.questionResponse,
    }));
    const selectRadios = question?.questionType?.find(
      (item) =>
        item.type === QUESTION_TYPE_CONSTANTS.MULTI_SELECT ||
        item?.type === QUESTION_TYPE_CONSTANTS.SINGLE_SELECT
    )?.response;
    setSelectedValues(
      (selectRadios?.length ? selectRadios : [selectRadios]) || []
    );
    setSnapShotsUploaded(question?.questionResponse?.evidence?.snapshot);
    setVideosUploaded(question?.questionResponse?.evidence?.video);
    setAudioUploaded(question?.questionResponse?.evidence?.audio);
  };

  useEffect(() => {
    if (question) setAllValuesOnMount();
    return () => setQuestionPayload({});
  }, [id]);

  useEffect(() => {
    const { evidence, response, evaluationStatus, counterMeasure, remarks } =
      questionPayload;
    if (
      evidence ||
      response?.length ||
      evaluationStatus ||
      counterMeasure ||
      remarks
    ) {
      remoteAuditDispatch(setQuestionPayloadInStore(questionPayload));
    }
  }, [questionPayload]);

  useEffect(() => {
    if (authState?.currentRole?.id !== userRole.TM && isRemote) {
      setQuestionPayload(remoteAuditState?.questionPayloadInStore);
      const selectedRadios =
        remoteAuditState?.questionPayloadInStore?.response?.[0]?.response;
      setSelectedValues(
        Array.isArray(selectedRadios) ? selectedRadios : [selectedRadios]
      );
      const { evidence } = remoteAuditState?.questionPayloadInStore;
      if (evidence) {
        setSnapShotsUploaded(evidence?.snapshot);
        setVideosUploaded(evidence?.video);
        setAudioUploaded(evidence?.audio);
      }
    }
  }, [remoteAuditState?.questionPayloadInStore]);

  useEffect(() => {
    if (isAuditPreview || isRemote) {
      setAllValuesOnMount();
    }
    if (isRemote)
      return () => {
        setQuestionPayload({});
      };
  }, [question]);

  const onMediaChanged = (uploadedSnapShots, key) => {
    setQuestionPayload((prev) => ({
      ...prev,
      evidence: { ...questionPayload.evidence, [key]: uploadedSnapShots },
    }));
  };

  const isNgOrImprovement =
    questionPayload?.evaluationStatus === EVALUATION_STATUS_CONSTANTS.NG ||
    questionPayload?.evaluationStatus ===
      EVALUATION_STATUS_CONSTANTS.IMPROVEMENT;

  useEffect(() => {
    if (isAudit || isAuditPreview) {
      setBorderAlert(questionPayload?.evaluationStatus);
    }
  }, [questionPayload?.evaluationStatus]);

  const fetchResponseId = (responseId) => {
    setQuestionPayload((prev) => ({ ...prev, responseId }));
  };
  return (
    <ThreeRow
      onBlur={() => {
        mode === "edit" && payloadChanged && handleSaveQuestionnaire();
      }}
      style={{ gap: variables.gapSmaller, overflow: "unset" }}
      topSection={
        <TwoRow
          style={AuditQuestionnaireContainerStyle.mainTwoColumnStyle}
          topSectionStyle={{ flex: 1 }}
          topSection={
            <TwoRow
              style={AuditQuestionnaireContainerStyle.questionWrapperStyle}
              topSection={
                <TwoColumn
                  style={{
                    alignItems: "center",
                  }}
                  leftSection={
                    <div
                      style={AuditQuestionnaireContainerStyle.inputWrapperStyle}
                    >
                      <Text
                        type="secondary"
                        className={QuestionStyle["section-index-heading-style"]}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          lineHeight: variables.lineHeightXlarge,
                        }}
                      >
                        {String.fromCharCode(96 + question?.sequence)}.
                        {question?.isMandatory && (
                          <span
                            style={{
                              color: variables.primaryBg,
                              fontWeight: variables.fontWeightNormal,
                            }}
                          >
                            *
                          </span>
                        )}
                      </Text>
                      <Input.TextArea
                        disabled={true}
                        autoSize={{ minRows: 1 }}
                        className={QuestionStyle["name-input-style"]}
                        value={question?.name}
                      />
                    </div>
                  }
                  leftSectionStyle={{ flex: 1 }}
                  /** Uncomment this to revive question type functionality */

                  // rightSectionStyle={{ width: "fit-content" }}
                  // rightSection={
                  //   <Tag style={AuditQuestionnaireContainerStyle.tagStyle}>
                  //     {
                  //       questionnaireSelectOptions.filter(
                  //         (item) =>
                  //           item.value === question?.questionType?.[0]?.type
                  //       )[0]?.label
                  //     }
                  //   </Tag>
                  // }
                />
              }
              bottomSectionStyle={
                AuditQuestionnaireContainerStyle.indentShiftStyle
              }
              bottomSection={
                <TwoRow
                  style={{
                    ...AuditQuestionnaireContainerStyle.overflowUnsetStyle,
                    ...(question?.questionType?.length > 1 &&
                      AuditQuestionnaireContainerStyle.smallerGapStyle),
                  }}
                  topSectionStyle={
                    AuditQuestionnaireContainerStyle.responseOptionsWrapperStyle
                  }
                  topSection={
                    question?.questionType?.some?.((questionTypeItem) => {
                      return (
                        questionTypeItem.type ===
                          QUESTION_TYPE_CONSTANTS.MULTI_SELECT ||
                        questionTypeItem.type ===
                          QUESTION_TYPE_CONSTANTS.SINGLE_SELECT
                      );
                    }) &&
                    question?.questionType[0]?.questionValue?.map((item) => {
                      return (
                        <Radio
                          key={item.id}
                          onClick={() => handleCheckboxChange(item.id)}
                          value={item.id}
                          checked={selectedValues?.includes(item.id)}
                          style={AuditQuestionnaireContainerStyle.radioStyle}
                          disabled={mode === "view"}
                        >
                          <Text
                            onClick={() => handleCheckboxChange(item.id)}
                            type="secondary"
                            style={
                              AuditQuestionnaireContainerStyle.radioLabelStyle
                            }
                          >
                            {item.value}
                          </Text>
                        </Radio>
                      );
                    })
                  }
                  bottomSection={
                    question?.questionType?.some(
                      (item) => item.type === QUESTION_TYPE_CONSTANTS.TEXTBOX
                    ) ? (
                      <Input
                        className={`${QuestionStyle["name-input-style"]} ${
                          mode === "view" &&
                          QuestionStyle["name-input-style-pseudo-disabled"]
                        }`}
                        placeholder={getLocalizedMessage(
                          intl,
                          "manageAudit.F2F.enterResponsePlaceholder"
                        )}
                        disabled={mode === "view"}
                        style={
                          mode === "view" &&
                          !questionPayload?.response?.find(
                            (item) =>
                              item.componentId ===
                              question?.questionType?.find(
                                (questionTypeItem) =>
                                  questionTypeItem.type ===
                                  QUESTION_TYPE_CONSTANTS.TEXTBOX
                              )?.id
                          )?.response && { color: variables.grey4 }
                        }
                        onChange={handleTextBoxChange}
                        value={
                          questionPayload?.response?.find(
                            (resItem) =>
                              resItem.componentId ===
                              question?.questionType?.find(
                                (questionTypeItem) =>
                                  questionTypeItem.type ===
                                  QUESTION_TYPE_CONSTANTS.TEXTBOX
                              )?.id
                          )?.response
                        }
                      />
                    ) : (
                      (isDnsd || auditMedium === "Self") && (
                        <Input
                          className={`${QuestionStyle["name-input-style"]} ${
                            !isDnsd &&
                            QuestionStyle["name-input-style-pseudo-disabled"]
                          }`}
                          placeholder={getLocalizedMessage(
                            intl,
                            "manageAudit.self.enterResponsePlaceholder"
                          )}
                          disabled={mode === "view" || !isDnsd}
                          onChange={(e) =>
                            handleKeyValuePairs("textResponse", e.target.value)
                          }
                          value={questionPayload?.textResponse}
                        />
                      )
                    )
                  }
                />
              }
            />
          }
          bottomSection={
            <TwoColumn
              id={`div-id${question?.id}`}
              style={{
                ...AuditQuestionnaireContainerStyle.overflowUnsetStyle,
                ...{ alignItems: "flex-end" },
                ...(isNgOrImprovement &&
                  AuditQuestionnaireContainerStyle.smallerGapStyle),
              }}
              leftSection={
                !isDnsd && (
                  <div>
                    <CustomSelect
                      disabled={mode === "view"}
                      getPopupContainer={() =>
                        document.getElementById(`div-id${question?.id}`)
                      }
                      placement="bottomLeft"
                      label={getLocalizedMessage(
                        intl,
                        "manageAudit.F2F.evaluationStatusHeading"
                      )}
                      restStyle={
                        responsive.isSm
                          ? {
                              minWidth: variables.evaluationStatusDropdownWidth,
                            }
                          : { marginBottom: variables.marginMedium2 }
                      }
                      required={question?.isMandatory}
                      customLabelStyle={
                        AuditQuestionnaireContainerStyle.customSelectLabelStyle
                      }
                      showSearch={false}
                      type="selectDropdown"
                      value={questionPayload?.evaluationStatus}
                      placeholder={getLocalizedMessage(
                        intl,
                        "manageAudit.F2F.evaluationStatusPlaceholder"
                      )}
                      onChange={(value) => {
                        handleKeyValuePairs("evaluationStatus", value);
                      }}
                      allowClear={true}
                      options={EVALUATION_STATUS_OPTIONS?.map((option) => {
                        return {
                          label: (
                            <Text
                              className={`${QuestionStyle["select-dropdown-item-style"]}`}
                            >
                              {option.label}
                            </Text>
                          ),
                          value: option.value,
                        };
                      })}
                    />
                    {!isDnsd && !responsive.isSm && isNgOrImprovement && (
                      <AddCounterMeasureContainer
                        className={`${QuestionStyle["name-input-style"]} ${
                          mode === "view" &&
                          QuestionStyle["name-input-style-pseudo-disabled"]
                        }`}
                        value={questionPayload?.counterMeasure}
                        onChange={(e) =>
                          handleKeyValuePairs("counterMeasure", e.target.value)
                        }
                      />
                    )}
                  </div>
                )
              }
              rightSectionStyle={{
                flex: 1,
              }}
              rightSection={
                !isDnsd &&
                responsive.isSm &&
                isNgOrImprovement && (
                  <AddCounterMeasureContainer
                    className={`${QuestionStyle["name-input-style"]} ${
                      mode === "view" &&
                      QuestionStyle["name-input-style-pseudo-disabled"]
                    }`}
                    value={questionPayload?.counterMeasure}
                    onChange={(e) =>
                      handleKeyValuePairs("counterMeasure", e.target.value)
                    }
                  />
                )
              }
            />
          }
        />
      }
      middleSection={
        !isDnsd && (
          <Remarks
            disabled={mode === "view"}
            evaluationStatus={questionPayload?.evaluationStatus}
            notFoundPlaceholder={getLocalizedMessage(
              intl,
              "manageAudit.notFound"
            )}
            getPopupContainer={() =>
              document.getElementById(`div-id${question?.id}`)
            }
            inputPlaceholder={getLocalizedMessage(
              intl,
              "manageAudit.addRemark"
            )}
            inputValue={questionPayload?.remarks}
            onInputChange={(value) => handleKeyValuePairs("remarks", value)}
          />
        )
      }
      bottomSectionStyle={{
        ...(mode === "view" &&
          !audioUploaded?.length &&
          !snapShotsUploaded?.length &&
          !videosUploaded?.length && { display: "none" }),
      }}
      bottomSection={
        <ThreeColumn
          style={{
            gap: variables.gapSmaller,
            justifyContent: variables.spaceBetween,
            flexWrap: variables.flexWrap,
          }}
          leftSectionStyle={{
            ...(mode === "view" &&
              !audioUploaded?.length && { display: "none" }),
          }}
          leftSection={
            <AudioContainer
              audioUploaded={audioUploaded}
              setAudioUploaded={setAudioUploaded}
              setEvidenceInProgress={setEvidenceInProgress}
              evidenceInProgress={evidenceInProgress}
              hideAction={mode === "view"}
              questionId={question?.id}
              question={question}
              onMediaChanged={onMediaChanged}
              fetchResponseId={fetchResponseId}
              responseId={questionPayload?.responseId}
            />
          }
          middleSectionStyle={{
            ...(mode === "view" &&
              !snapShotsUploaded?.length && { display: "none" }),
          }}
          middleSection={
            <MediaContainer
              mediaUploaded={snapShotsUploaded}
              setMediaUploaded={setSnapShotsUploaded}
              evidenceInProgress={evidenceInProgress}
              hideAction={mode === "view"}
              questionId={question?.id}
              question={question}
              onMediaChanged={onMediaChanged}
              fetchResponseId={fetchResponseId}
              responseId={questionPayload?.responseId}
            />
          }
          rightSectionStyle={{
            ...(mode === "view" &&
              !videosUploaded?.length && { display: "none" }),
          }}
          rightSection={
            <MediaContainer
              isVideoCapturing={true}
              mediaUploaded={videosUploaded}
              setMediaUploaded={setVideosUploaded}
              evidenceInProgress={evidenceInProgress}
              hideAction={mode === "view"}
              questionId={question?.id}
              question={question}
              onMediaChanged={onMediaChanged}
              fetchResponseId={fetchResponseId}
              responseId={questionPayload?.responseId}
            />
          }
        />
      }
    />
  );
};

export default AuditQuestionnaireContainer;
